import React, { useEffect, useState } from "react";
import { useParams, Link } from 'react-router-dom';
import toast from 'react-hot-toast';
import dayjs from 'dayjs';
import BTable from 'react-bootstrap/Table';
import Accordion from 'react-bootstrap/Accordion';
import Button from "react-bootstrap/Button";
import Breadcrumb from 'react-bootstrap/Breadcrumb';
import OverlayTrigger from 'react-bootstrap/OverlayTrigger';
import Tooltip  from 'react-bootstrap/Tooltip';
import { FaTimesCircle, FaCheckCircle } from "react-icons/fa";
import Skeleton from 'react-loading-skeleton';
import 'react-loading-skeleton/dist/skeleton.css';
import { PatientModel, PrescriptionModel, SamcartModel, CronEventsModel } from "../../models";
import { PatientInterface, PrescriptionInterface, SamcartInterface, CronEventsInterface } from "../../interfaces";
import { PrescriptionCard, PatientTable, ConfirmModal } from "../../components";
import { normalizeAndCapitalize } from "../../utils";

interface ConfirmModalState {
  show: boolean;
  title: string;
  message: string;
  confirmText?: string;
  confirmVariant?: 'primary' | 'danger' | 'success' | 'warning';
  onConfirm: () => void;
}

interface RefillURL {
  _id: string;
  createdAt: string;
  onboardingUrl: string;
  status: string;
}

const ViewPatientProfile = () => {
  const [patient, setPatient] = useState<PatientInterface>();
  const [prescriptions, setPrescriptions] = useState<PrescriptionInterface[]>([]);
  const [transactions, setTransactions] = useState<SamcartInterface[]>([]);
  const [refillUrls, setRefillUrls] = useState<RefillURL[]>([]);
  const [confirmModal, setConfirmModal] = useState<ConfirmModalState>({
    show: false,
    title: '',
    message: '',
    onConfirm: () => {},
  });

  const { id } = useParams();

  const closeConfirmModal = () => {
    setConfirmModal(prev => ({ ...prev, show: false }));
  };

  const getPatient = async (id: string) => {
    const patient = await PatientModel.get(id);
    setPatient(patient);
  };

  const getPatientPrescriptions = async (id: string) => {
    const prescriptions = await PrescriptionModel.getUserPrescriptions(id);
    setPrescriptions(prescriptions?.data);
  };

  const getPatientTransactions = async (id: string) => {
    const transactions = await SamcartModel.getUserTransactions(id);
    setTransactions(transactions?.data);
  };

  const getPatientCronEvents = async (id: string) => {
    const events: CronEventsInterface[] = await CronEventsModel.fetchAll({ internalPatientId: id, eventType: 'refill_created', $sort: { 'data.created_at': -1 } });

    if(events && events.length > 0) {
      const refillUrls = events.reduce((acc, event) => {
        if (event.data) {
          acc.push({
            _id: event._id,
            createdAt: event.data.created_at,
            onboardingUrl: event.data.onboarding_url,
            status: event.status,
          });
        }
        return acc;
      }, [] as RefillURL[]);
      setRefillUrls(refillUrls);
    }
  };

  const updatePatient = async (id:string, patient: PatientInterface) => {
    const update = await PatientModel.update(id, patient);
    setPatient(update);
  };

  const updateTestStatus = () => { 
    if(!patient) return;
    const isTest = !patient.test; // Toggle test status
    
    setConfirmModal({
      show: true,
      title: isTest ? 'Mark as Test' : 'Mark as Regular',
      message: `Do you want to ${isTest ? 'mark' : 'unmark'} this patient as a test patient?`,
      confirmText: isTest ? 'Mark as Test' : 'Mark as Regular',
      confirmVariant: isTest ? 'warning' : 'success',
      onConfirm: async () => {
        closeConfirmModal();
        const updatedPatient = await PatientModel.update(patient._id, { ...patient, test: isTest });
        setPatient(updatedPatient);
        toast.success(`Patient marked as ${isTest ? 'test' : 'regular'} successfully!`);
      },
    });
  };

  const manualVoucherTrigger = () => {
    if (!patient?.patientId) return;
    
    setConfirmModal({
      show: true,
      title: 'Trigger Voucher Refill',
      message: 'Do you want to manually trigger a voucher refill for this patient?',
      confirmText: 'Trigger Refill',
      onConfirm: () => {
        closeConfirmModal();
        toast.promise(
          PatientModel.voucherRefill(patient.patientId),
          {
            loading: 'Issuing refill voucher...',
            success: <b>Refill Successful!</b>,
            error: <b>Refill Failed</b>,
          }
        );
      },
    });
  };

  const manualCronComplete = (eventId: string) => {
    if (!id) return;
    
    setConfirmModal({
      show: true,
      title: 'Complete Cron Event',
      message: 'Do you want to manually complete this cron event?',
      confirmText: 'Complete',
      onConfirm: () => {
        closeConfirmModal();
        toast.promise(
          (async () => {
            await CronEventsModel.update(eventId, { status: 'completed' });
            await getPatientCronEvents(id);
          })(),
          {
            loading: 'Completing cron event...',
            success: <b>Cron Event Completed!</b>,
            error: <b>Failed to Complete Cron Event</b>,
          }
        );
      },
    });
  };

  useEffect(() => {
    if (id) {
      getPatient(id);
      getPatientPrescriptions(id);
      getPatientTransactions(id);
      getPatientCronEvents(id);
    }
  }, [id]);

  return (<>
    <div className="p-2">
      <Breadcrumb>
        <Breadcrumb.Item linkAs={Link} linkProps={{ to: '/patients' }}>
          Patients
        </Breadcrumb.Item>
        <Breadcrumb.Item active>
          {patient ? `${patient.firstName} ${patient.lastName}` : 'Loading...'}
        </Breadcrumb.Item>
      </Breadcrumb>
      <div>
        <div className="d-flex align-items-center w-100">
          {patient ? 
            <div className="d-flex align-items-center">
              <h2 className="mb-3">
                {`${patient?.firstName} ${patient?.lastName} (${patient?.email})`}
              </h2>
              <OverlayTrigger
                trigger={['hover', 'focus']}
                placement="right"
                overlay={
                  <Tooltip id="tooltip-id">
                    {patient?.test
                      ? 'This is a test entry. Click to flag as real.'
                      : 'This is a real entry. Click to flag as test.'}
                  </Tooltip>
                }
              >
                <span className="cursor-pointer d-flex">
                  {patient?.test ? (
                    <FaTimesCircle color="red" size="1.4rem" onClick={updateTestStatus} />
                  ) : (
                    <FaCheckCircle color="green" size="1.4rem" onClick={updateTestStatus} />
                  )}
                </span>
              </OverlayTrigger>
            </div>
            : 
            <Skeleton className="mb-2 ml-2" width={500} height={50} />
          }
        </div>
        <div>
          {patient ? (
            <div>
              <PatientTable patient={patient} onSave={(patient) => updatePatient(patient._id, patient)}/>
            </div>
          ) : (
            <Skeleton count={9} height={30}/>
          )}
        </div>
        <div className="d-flex justify-content-end">
          <Button
            variant="primary"
            disabled={!patient}
            onClick={manualVoucherTrigger}
          >
            Trigger Manual Voucher Refill
          </Button>
        </div>
        <div className="mt-3">
          {prescriptions.length > 0 && (
            <div className="fw-bolder mb-3">
						Prescription History
            </div>
          )}
          <Accordion defaultActiveKey="0">
            {prescriptions?.map((pres, i) => (
              <Accordion.Item key={i} eventKey={i.toString()}>
                <Accordion.Header>{`Prescription on ${dayjs(pres.createdAt).format("MMM DD, YYYY")}`}</Accordion.Header>
                <Accordion.Body>
                  <div className="d-flex">
                    {pres.prescriptions.map((item, i) => (
                      <PrescriptionCard key={i} prescription={item} />
                    ))}
                  </div>
                </Accordion.Body>
              </Accordion.Item>
            ))}
          </Accordion>
        </div>
        <div className="mt-3">
          {transactions.length > 0 && (
            <div className="fw-bolder mb-3">
						Refill URLs
            </div>
          )}
          <Accordion defaultActiveKey="0">
            {refillUrls?.map((item, i) => (
              <Accordion.Item key={i} eventKey={i.toString()}>
                <Accordion.Header>{
                  `Refill on ${dayjs(item.createdAt).format("MMM DD, YYYY")} ${item.status === 'waiting' ? '(waiting)' : ''}`
                }</Accordion.Header>
                <Accordion.Body>
                  <BTable striped bordered hover responsive size="sm">
                    <tbody>
                      <tr>
                        <td>URL</td>
                        <td>{item.onboardingUrl}</td>
                      </tr>
                      <tr>
                        <td>Created At</td>
                        <td>{dayjs(item.createdAt).format("MMM DD, YYYY")}</td>
                      </tr>
                    </tbody>
                  </BTable>
                  {item.status === 'waiting' && (
                    <Button
                      variant="primary"
                      onClick={() => manualCronComplete(item._id)}
                    >
                      Manually Complete
                    </Button>
                  )}
                </Accordion.Body>
              </Accordion.Item>
            ))}
          </Accordion>
        </div>
        <div className="mt-3">
          {transactions.length > 0 && (
            <div className="fw-bolder mb-3">
						Transaction History
            </div>
          )}
          <Accordion defaultActiveKey="0">
            {transactions?.map((item, i) => (
              <Accordion.Item key={i} eventKey={i.toString()}>
                <Accordion.Header>{normalizeAndCapitalize(item?.payload?.type || "Missing Name...")} on {dayjs(item?.payload?.order.created_at || item?.createdAt).format("MMM DD, YYYY")}</Accordion.Header>
                <Accordion.Body>
                  <BTable striped bordered hover responsive size="sm">
                    <tbody>
                      <tr>
                        <td>Name</td>
                        <td>{item?.payload?.product?.name}</td>
                      </tr>
                      <tr>
                        <td>Price</td>
                        <td>{item?.payload?.product?.price}</td>
                      </tr>
                      <tr>
                        <td>Quantity</td>
                        <td>{item?.payload?.product?.quantity || 1}</td>
                      </tr>
                      <tr>
                        <td>Created At</td>
                        <td>{dayjs(item?.payload?.order.created_at).format("MMM DD, YYYY")}</td>
                      </tr>
                    </tbody>
                  </BTable>
                </Accordion.Body>
              </Accordion.Item>
            ))}
          </Accordion>
        </div>
      </div>
    </div>

    <ConfirmModal
      show={confirmModal.show}
      title={confirmModal.title}
      message={confirmModal.message}
      confirmText={confirmModal.confirmText}
      confirmVariant={confirmModal.confirmVariant}
      onConfirm={confirmModal.onConfirm}
      onCancel={closeConfirmModal}
    />
  </>);
};

export default ViewPatientProfile;
